import { useCallback, useState } from 'react';
import { usePapaParse } from 'react-papaparse';

import { UploadCsvStatusEnum } from '../../../../constants/addressBook';
import { TParsedData } from '../../../../types/addressBook';

interface IProps {
  requiredColumnCount: number;
}

export const useCsvUpload = ({ requiredColumnCount }: IProps) => {
  const { readString } = usePapaParse();
  const [fileCSV, setFileCSV] = useState<File | null>(null);
  const [dataAddresses, setDataAddresses] = useState<TParsedData[] | null>(null);
  const [status, setStatus] = useState<UploadCsvStatusEnum>(UploadCsvStatusEnum.Idle);

  const handleFileSizeError = useCallback(() => {
    setFileCSV(null);
    setStatus(UploadCsvStatusEnum.ErrorLargeFile);
  }, []);

  const validateHeaders = useCallback(
    (headers: string[]): boolean => {
      if (headers.length < requiredColumnCount) {
        return false;
      }

      return true;
    },
    [requiredColumnCount],
  );

  const isFileEmpty = useCallback((parsedData: TParsedData[]): boolean => {
    if (parsedData.length === 0) {
      return true;
    }

    // check if there is at least one row with data
    return !parsedData.some((row) => Object.values(row).some((value) => value && String(value).trim() !== ''));
  }, []);

  const handleDeleteFile = useCallback(() => {
    setFileCSV(null);
    setDataAddresses(null);
  }, []);

  const handleParseSuccess = useCallback(
    (file: File, parsedData: TParsedData[]) => {
      setFileCSV(file);
      setDataAddresses(parsedData);
      setStatus(UploadCsvStatusEnum.Complete);
    },
    [handleDeleteFile],
  );

  const handleParseError = useCallback(
    (errorType: UploadCsvStatusEnum) => {
      setStatus(errorType);
      handleDeleteFile();
    },
    [handleDeleteFile],
  );

  const handleReadError = useCallback(() => {
    setStatus(UploadCsvStatusEnum.Error);
  }, []);

  const readFileAsText = useCallback(
    (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          const csvText = event.target?.result as string;
          resolve(csvText);
        };

        reader.onerror = () => {
          handleReadError();
          reject();
        };

        reader.readAsText(file);
      });
    },
    [handleReadError],
  );

  const parseCsvFile = useCallback(
    (csvText: string, file: File): Promise<void> => {
      return new Promise((resolve, reject) => {
        readString(csvText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: string, index: number) => {
            // if header is empty, transform it to "Unnamed column # {index+1}"
            if (!header || header.trim() === '') {
              return `Unnamed column #${index + 1}`;
            }
            return header;
          },
          complete: (results) => {
            const headers = results.meta.fields ?? [];
            const parsedData = results.data as TParsedData[];

            if (!validateHeaders(headers)) {
              reject(UploadCsvStatusEnum.ErrorInvalidHeaders);
              return;
            }

            if (isFileEmpty(parsedData)) {
              reject(UploadCsvStatusEnum.ErrorNoRowsWithData);
              return;
            }

            handleParseSuccess(file, parsedData);
            resolve();
          },
          error: () => {
            reject(UploadCsvStatusEnum.ErrorInvalidHeaders);
          },
        });
      });
    },
    [readString, handleParseSuccess, validateHeaders, isFileEmpty],
  );

  const handleUploadFile = useCallback(
    async (file: File) => {
      setStatus(UploadCsvStatusEnum.Loading);

      try {
        const csvText = await readFileAsText(file);
        await parseCsvFile(csvText, file);
      } catch (error) {
        switch (error) {
          case UploadCsvStatusEnum.ErrorInvalidHeaders:
            handleParseError(UploadCsvStatusEnum.ErrorInvalidHeaders);
            break;
          case UploadCsvStatusEnum.ErrorNoRowsWithData:
            handleParseError(UploadCsvStatusEnum.ErrorNoRowsWithData);
            break;
          default:
            setStatus(UploadCsvStatusEnum.Error);
        }
      }
    },
    [readFileAsText, parseCsvFile, handleParseError],
  );

  return {
    fileCSV,
    dataAddresses,
    status,
    setStatus,
    handleUploadFile,
    handleDeleteFile,
    handleFileSizeError,
  };
};
